/**
 * Canonical error contracts for the Finance Platform.
 *
 * These are the shared shapes exposed to clients, UIs, and guard scripts.
 * HTTP status mapping lives in the API layer; this package is transport-agnostic.
 */
export const ERROR_CODES = [
    'AUTH_UNAUTHORIZED',
    'AUTH_FORBIDDEN',
    'AUTH_INVALID_CREDENTIALS',
    'AUTH_ACCOUNT_INACTIVE',
    'AUTH_TOKEN_EXPIRED',
    'AUTH_SESSION_EXPIRED',
    'AUTH_MFA_REQUIRED',
    'AUTH_TOKEN_REUSE',
    'AUTH_REFRESH_REUSED',
    'AUTH_CONTEXT_REQUIRED',
    'USER_ID_REQUIRED',
    'VALIDATION_FAILED',
    'UNPROCESSABLE_ENTITY',
    'NOT_FOUND',
    'CONFLICT',
    'PRECONDITION_FAILED',
    'LOCKED',
    'RATE_LIMIT_EXCEEDED',
    'INTERNAL_ERROR',
    'BAD_GATEWAY',
    'SERVICE_UNAVAILABLE',
    'GATEWAY_TIMEOUT',
    'CONTRACT_VIOLATION',
    'ALREADY_MEMBER',
    'INVALID_ROLE_IDS',
    'INVITATION_RATE_LIMIT_EXCEEDED',
    'INVITATION_ALREADY_PENDING',
    'USER_ALREADY_EXISTS',
    'INVITATION_INVALID_OR_EXPIRED',
    'INVITER_NOT_MEMBER',
    'INVITER_NOT_ADMIN',
    'REVOKER_NOT_MEMBER',
    'INVITATION_NOT_FOUND',
    'CROSS_ORGANIZATION_ACCESS_DENIED',
    'CROSS_ORG_ROLE_ASSIGNMENT_FORBIDDEN',
    'CROSS_BRANCH_ACCESS_DENIED',
    'DELEGATION_INVALID_OR_EXPIRED',
    'AUTH_DELEGATION_RESOURCE_DENIED',
    'INSUFFICIENT_PERMISSION_FOR_ROLE_ASSIGNMENT',
    'MFA_ADMIN_REQUIRES_AAL2',
    'MFA_NOT_ENABLED',
    'NOT_A_SUPPORT_DELEGATE',
    'NOT_MEMBER_OF_ORGANIZATION',
    'NO_ACCESS_TO_BRANCH',
    'ORGANIZATION_ID_REQUIRED',
    'ORG_SELF_MANAGEMENT_REQUIRES_ADMIN',
    'SUPPORT_SCOPE_MISMATCH',
    'SUPPORT_BRANCH_SCOPE_MISMATCH',
    'SYSTEM_SCOPE_REQUIRES_SYSTEM_ACTOR',
    'ACCOUNT_INACTIVE_OR_NOT_FOUND',
    'ACCOUNT_NOT_FOUND',
    'ACCOUNT_NOT_FOUND_OR_INACTIVE',
    'AUTH_CONTEXT_NOT_AVAILABLE',
    'AUTH_NO_ACTIVE_MEMBERSHIP',
    'FISCAL_PERIOD_LOCKED',
    'FISCAL_PERIOD_NOT_FOUND',
    'FISCAL_YEAR_CLOSED',
    'JOURNAL_ALREADY_REVERSED',
    'JOURNAL_LINE_INVALID_SIDES',
    'JOURNAL_LINE_NEGATIVE_CREDIT',
    'JOURNAL_LINE_NEGATIVE_DEBIT',
    'JOURNAL_NO_LINES',
    'JOURNAL_NOT_FOUND',
    'JOURNAL_NOT_FOUND_OR_NOT_POSTED',
    'JOURNAL_NOT_IN_DRAFT_STATE',
    'JOURNAL_REQUIRES_AT_LEAST_TWO_ACCOUNTS',
    'JOURNAL_UNBALANCED',
    'JOURNAL_VERSION_MISMATCH',
    'LEDGER_ACCOUNT_NOT_FOUND',
    'NO_FISCAL_PERIOD_FOR_REVERSAL_DATE',
    'REVERSAL_FISCAL_YEAR_CLOSED',
    'REVERSAL_PERIOD_LOCKED',
    'BRANCH_HAS_ACTIVE_CHILDREN',
    'BRANCH_LAST_USABLE_PROTECTED',
    'BRANCH_LIFECYCLE_REASON_REQUIRED',
    'BRANCH_MUST_BE_ACTIVE_TO_DEFAULT',
];
export const ERROR_CODE_KEYS = {
    AUTH_UNAUTHORIZED: 'errors.common.unauthorized',
    AUTH_FORBIDDEN: 'errors.common.forbidden',
    AUTH_INVALID_CREDENTIALS: 'errors.common.unauthorized',
    AUTH_ACCOUNT_INACTIVE: 'errors.common.unauthorized',
    AUTH_TOKEN_EXPIRED: 'errors.common.unauthorized',
    AUTH_SESSION_EXPIRED: 'errors.common.unauthorized',
    AUTH_MFA_REQUIRED: 'errors.common.forbidden',
    AUTH_TOKEN_REUSE: 'errors.common.unauthorized',
    AUTH_REFRESH_REUSED: 'errors.common.unauthorized',
    AUTH_CONTEXT_REQUIRED: 'errors.common.unauthorized',
    USER_ID_REQUIRED: 'errors.common.unauthorized',
    VALIDATION_FAILED: 'errors.common.badRequest',
    UNPROCESSABLE_ENTITY: 'errors.common.badRequest',
    NOT_FOUND: 'errors.common.notFound',
    CONFLICT: 'errors.common.badRequest',
    PRECONDITION_FAILED: 'errors.common.badRequest',
    LOCKED: 'errors.common.badRequest',
    RATE_LIMIT_EXCEEDED: 'errors.rateLimit.exceeded',
    INTERNAL_ERROR: 'errors.common.internalError',
    BAD_GATEWAY: 'errors.common.internalError',
    SERVICE_UNAVAILABLE: 'errors.common.internalError',
    GATEWAY_TIMEOUT: 'errors.common.internalError',
    CONTRACT_VIOLATION: 'errors.common.internalError',
    ALREADY_MEMBER: 'errors.common.badRequest',
    INVALID_ROLE_IDS: 'errors.common.badRequest',
    INVITATION_RATE_LIMIT_EXCEEDED: 'errors.common.badRequest',
    INVITATION_ALREADY_PENDING: 'errors.common.badRequest',
    USER_ALREADY_EXISTS: 'errors.common.badRequest',
    INVITATION_INVALID_OR_EXPIRED: 'errors.common.unauthorized',
    INVITER_NOT_MEMBER: 'errors.common.forbidden',
    INVITER_NOT_ADMIN: 'errors.common.forbidden',
    REVOKER_NOT_MEMBER: 'errors.common.forbidden',
    INVITATION_NOT_FOUND: 'errors.common.notFound',
    CROSS_ORGANIZATION_ACCESS_DENIED: 'errors.common.forbidden',
    CROSS_ORG_ROLE_ASSIGNMENT_FORBIDDEN: 'errors.common.forbidden',
    CROSS_BRANCH_ACCESS_DENIED: 'errors.common.forbidden',
    DELEGATION_INVALID_OR_EXPIRED: 'errors.common.forbidden',
    AUTH_DELEGATION_RESOURCE_DENIED: 'errors.common.forbidden',
    INSUFFICIENT_PERMISSION_FOR_ROLE_ASSIGNMENT: 'errors.common.forbidden',
    MFA_ADMIN_REQUIRES_AAL2: 'errors.common.forbidden',
    MFA_NOT_ENABLED: 'errors.common.badRequest',
    NOT_A_SUPPORT_DELEGATE: 'errors.common.forbidden',
    NOT_MEMBER_OF_ORGANIZATION: 'errors.common.forbidden',
    NO_ACCESS_TO_BRANCH: 'errors.common.forbidden',
    ORGANIZATION_ID_REQUIRED: 'errors.common.forbidden',
    ORG_SELF_MANAGEMENT_REQUIRES_ADMIN: 'errors.common.forbidden',
    SUPPORT_SCOPE_MISMATCH: 'errors.common.forbidden',
    SUPPORT_BRANCH_SCOPE_MISMATCH: 'errors.common.forbidden',
    SYSTEM_SCOPE_REQUIRES_SYSTEM_ACTOR: 'errors.common.forbidden',
    ACCOUNT_INACTIVE_OR_NOT_FOUND: 'errors.common.badRequest',
    ACCOUNT_NOT_FOUND: 'errors.common.badRequest',
    ACCOUNT_NOT_FOUND_OR_INACTIVE: 'errors.common.badRequest',
    AUTH_CONTEXT_NOT_AVAILABLE: 'errors.common.unauthorized',
    AUTH_NO_ACTIVE_MEMBERSHIP: 'errors.common.unauthorized',
    FISCAL_PERIOD_LOCKED: 'errors.common.conflict',
    FISCAL_PERIOD_NOT_FOUND: 'errors.common.notFound',
    FISCAL_YEAR_CLOSED: 'errors.common.conflict',
    JOURNAL_ALREADY_REVERSED: 'errors.common.badRequest',
    JOURNAL_LINE_INVALID_SIDES: 'errors.common.badRequest',
    JOURNAL_LINE_NEGATIVE_CREDIT: 'errors.common.badRequest',
    JOURNAL_LINE_NEGATIVE_DEBIT: 'errors.common.badRequest',
    JOURNAL_NO_LINES: 'errors.common.badRequest',
    JOURNAL_NOT_FOUND: 'errors.common.notFound',
    JOURNAL_NOT_FOUND_OR_NOT_POSTED: 'errors.common.badRequest',
    JOURNAL_NOT_IN_DRAFT_STATE: 'errors.common.badRequest',
    JOURNAL_REQUIRES_AT_LEAST_TWO_ACCOUNTS: 'errors.common.badRequest',
    JOURNAL_UNBALANCED: 'errors.common.badRequest',
    JOURNAL_VERSION_MISMATCH: 'errors.common.badRequest',
    LEDGER_ACCOUNT_NOT_FOUND: 'errors.common.notFound',
    NO_FISCAL_PERIOD_FOR_REVERSAL_DATE: 'errors.common.badRequest',
    REVERSAL_FISCAL_YEAR_CLOSED: 'errors.common.conflict',
    REVERSAL_PERIOD_LOCKED: 'errors.common.conflict',
    BRANCH_HAS_ACTIVE_CHILDREN: 'errors.branch.hasActiveChildren',
    BRANCH_LAST_USABLE_PROTECTED: 'errors.branch.lastUsableProtected',
    BRANCH_LIFECYCLE_REASON_REQUIRED: 'errors.branch.lifecycleReasonRequired',
    BRANCH_MUST_BE_ACTIVE_TO_DEFAULT: 'errors.branch.mustBeActiveToDefault',
};
export const ERROR_CODE_SEVERITY = {
    AUTH_UNAUTHORIZED: 'error',
    AUTH_FORBIDDEN: 'error',
    AUTH_INVALID_CREDENTIALS: 'error',
    AUTH_ACCOUNT_INACTIVE: 'error',
    AUTH_TOKEN_EXPIRED: 'error',
    AUTH_SESSION_EXPIRED: 'error',
    AUTH_MFA_REQUIRED: 'warning',
    AUTH_TOKEN_REUSE: 'error',
    AUTH_REFRESH_REUSED: 'error',
    AUTH_CONTEXT_REQUIRED: 'warning',
    USER_ID_REQUIRED: 'error',
    VALIDATION_FAILED: 'warning',
    UNPROCESSABLE_ENTITY: 'warning',
    NOT_FOUND: 'warning',
    CONFLICT: 'warning',
    PRECONDITION_FAILED: 'warning',
    LOCKED: 'warning',
    RATE_LIMIT_EXCEEDED: 'warning',
    INTERNAL_ERROR: 'error',
    BAD_GATEWAY: 'error',
    SERVICE_UNAVAILABLE: 'error',
    GATEWAY_TIMEOUT: 'error',
    CONTRACT_VIOLATION: 'error',
    ALREADY_MEMBER: 'warning',
    INVALID_ROLE_IDS: 'error',
    INVITATION_RATE_LIMIT_EXCEEDED: 'warning',
    INVITATION_ALREADY_PENDING: 'warning',
    USER_ALREADY_EXISTS: 'warning',
    INVITATION_INVALID_OR_EXPIRED: 'error',
    INVITER_NOT_MEMBER: 'error',
    INVITER_NOT_ADMIN: 'error',
    REVOKER_NOT_MEMBER: 'error',
    INVITATION_NOT_FOUND: 'warning',
    CROSS_ORGANIZATION_ACCESS_DENIED: 'error',
    CROSS_ORG_ROLE_ASSIGNMENT_FORBIDDEN: 'error',
    CROSS_BRANCH_ACCESS_DENIED: 'error',
    DELEGATION_INVALID_OR_EXPIRED: 'error',
    AUTH_DELEGATION_RESOURCE_DENIED: 'error',
    INSUFFICIENT_PERMISSION_FOR_ROLE_ASSIGNMENT: 'error',
    MFA_ADMIN_REQUIRES_AAL2: 'error',
    MFA_NOT_ENABLED: 'error',
    NOT_A_SUPPORT_DELEGATE: 'error',
    NOT_MEMBER_OF_ORGANIZATION: 'error',
    NO_ACCESS_TO_BRANCH: 'error',
    ORGANIZATION_ID_REQUIRED: 'error',
    ORG_SELF_MANAGEMENT_REQUIRES_ADMIN: 'error',
    SUPPORT_SCOPE_MISMATCH: 'error',
    SUPPORT_BRANCH_SCOPE_MISMATCH: 'error',
    SYSTEM_SCOPE_REQUIRES_SYSTEM_ACTOR: 'error',
    ACCOUNT_INACTIVE_OR_NOT_FOUND: 'warning',
    ACCOUNT_NOT_FOUND: 'warning',
    ACCOUNT_NOT_FOUND_OR_INACTIVE: 'warning',
    AUTH_CONTEXT_NOT_AVAILABLE: 'error',
    AUTH_NO_ACTIVE_MEMBERSHIP: 'error',
    FISCAL_PERIOD_LOCKED: 'warning',
    FISCAL_PERIOD_NOT_FOUND: 'warning',
    FISCAL_YEAR_CLOSED: 'warning',
    JOURNAL_ALREADY_REVERSED: 'warning',
    JOURNAL_LINE_INVALID_SIDES: 'warning',
    JOURNAL_LINE_NEGATIVE_CREDIT: 'warning',
    JOURNAL_LINE_NEGATIVE_DEBIT: 'warning',
    JOURNAL_NO_LINES: 'warning',
    JOURNAL_NOT_FOUND: 'warning',
    JOURNAL_NOT_FOUND_OR_NOT_POSTED: 'warning',
    JOURNAL_NOT_IN_DRAFT_STATE: 'warning',
    JOURNAL_REQUIRES_AT_LEAST_TWO_ACCOUNTS: 'warning',
    JOURNAL_UNBALANCED: 'warning',
    JOURNAL_VERSION_MISMATCH: 'warning',
    LEDGER_ACCOUNT_NOT_FOUND: 'warning',
    NO_FISCAL_PERIOD_FOR_REVERSAL_DATE: 'warning',
    REVERSAL_FISCAL_YEAR_CLOSED: 'warning',
    REVERSAL_PERIOD_LOCKED: 'warning',
    BRANCH_HAS_ACTIVE_CHILDREN: 'warning',
    BRANCH_LAST_USABLE_PROTECTED: 'warning',
    BRANCH_LIFECYCLE_REASON_REQUIRED: 'warning',
    BRANCH_MUST_BE_ACTIVE_TO_DEFAULT: 'warning',
};
export function createErrorContract(code, message, context) {
    return {
        code,
        key: ERROR_CODE_KEYS[code],
        message,
        severity: ERROR_CODE_SEVERITY[code],
        context,
    };
}
